import type { FernieEntityContext, FernieEntityType } from "~/store/fernieActionSlice";

import { resolveOrchestratorBase } from "~/services/aiOrchestrator";

export type LookIngressSource = "phoneCamera";

export type LookEntityType = Extract<
	FernieEntityType,
	"customer" | "item" | "masterOrder" | "materialItem" | "order" | "purchaseOrder"
>;

export interface LookResolvedEntity {
	displayName: string;
	entityId: string;
	entityType: LookEntityType;
	matchSource?: "barcode" | "ocr" | "sku";
}

export interface LookResolveRequest {
	imageBase64: string;
	ingressSource: LookIngressSource;
	mimeType: string;
	sessionId?: string | null;
}

export type LookResolveOutcome =
	| {
			candidates: LookResolvedEntity[];
			clarifyingQuestion: string | null;
			kind: "ambiguous";
	  }
	| {
			clarifyingQuestion: string | null;
			kind: "unresolved";
	  }
	| {
			entity: LookResolvedEntity;
			extractedText?: string;
			kind: "resolved";
	  };

export type LookAmbiguousOutcome = Extract<LookResolveOutcome, { kind: "ambiguous" }>;

const LOOK_RESOLVE_PATH = "/look/resolve";

const LOOK_INGRESS_SOURCE: LookIngressSource = "phoneCamera";

const resolveLookEndpoint = (graphqlUrl: string): string => {
	return `${resolveOrchestratorBase(graphqlUrl)}${LOOK_RESOLVE_PATH}`;
};

const isLookEntityType = (value: unknown): value is LookEntityType =>
	value === "customer" ||
	value === "item" ||
	value === "masterOrder" ||
	value === "materialItem" ||
	value === "order" ||
	value === "purchaseOrder";

const MAX_LISTED_CANDIDATES = 5;

class LookResolveHttpError extends Error {
	readonly response: { status: number };

	constructor(status: number, message: string) {
		super(message);
		this.name = "LookResolveHttpError";
		this.response = { status };
	}
}

const isLookUnavailableError = (error: unknown): boolean =>
	error instanceof LookResolveHttpError && error.response.status === 404;

const toEntityContext = (entity: LookResolvedEntity): FernieEntityContext => ({
	displayName: entity.displayName || undefined,
	entityId: entity.entityId,
	entityType: entity.entityType
});

const listCandidates = (outcome: LookAmbiguousOutcome, question: string): string => {
	const lines = outcome.candidates
		.slice(0, MAX_LISTED_CANDIDATES)
		.map((candidate, index) => `${index + 1}. ${candidate.displayName || candidate.entityId}`)
		.join("\n");
	return lines ? `${question}\n\n${lines}` : question;
};

export default {
	isLookEntityType,
	isLookUnavailableError,
	listCandidates,
	LOOK_INGRESS_SOURCE,
	LookResolveHttpError,
	resolveLookEndpoint,
	toEntityContext
};
